import { useState } from 'react';
import { ActCard } from './ActCard';
import { dayLabelLong, isPast } from '@/lib/actHelpers';
import type { ActivityDayGroup } from '@/lib/actHelpers';
import styles from './DaySection.module.css';

interface DaySectionProps {
  slug: string;
  group: ActivityDayGroup;
}

/**
 * Eine Tagesgruppe im Plan. Vergangene Acts sind standardmäßig eingeklappt,
 * damit man sofort das nächste Aktuelle sieht. Laufende Acts (isPast === false)
 * gelten nie als vergangen und bleiben immer sichtbar.
 *
 *  - Komplett vergangener Tag: ganzer Abschnitt zugeklappt, Datums-Pill als
 *    Button mit Count + Chevron.
 *  - Gemischter/zukünftiger Tag: sichtbare Acts immer da; vergangene hinter
 *    einem "N vergangene Acts anzeigen"-Toggle, das sie chronologisch davor
 *    einblendet.
 */
export function DaySection({ slug, group }: DaySectionProps) {
  const [expanded, setExpanded] = useState(false);

  // group.acts ist bereits chronologisch sortiert (groupActsByDay).
  const past = group.acts.filter((a) => isPast(a));
  const visible = group.acts.filter((a) => !isPast(a));

  const label = group.date ? dayLabelLong(group.date) : 'Ohne Datum';
  const fullyPast = visible.length === 0 && past.length > 0;

  // Komplett vergangener Tag: Datums-Pill selbst ist der Aufklapp-Button.
  if (fullyPast) {
    return (
      <section className={styles.daySection}>
        <button
          type="button"
          className={styles.collapsedHeader}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className={styles.dayLabelPill}>{label}</span>
          <span className={styles.count}>{past.length}</span>
          <span className={styles.dayLabelRail} />
          <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>▾</span>
        </button>
        {expanded ? (
          <div className={styles.dayActs}>
            {past.map((act) => (
              <ActCard key={act.id} slug={slug} act={act} />
            ))}
          </div>
        ) : null}
      </section>
    );
  }

  // Gemischter/zukünftiger Tag: sichtbare Acts immer, vergangene optional davor.
  return (
    <section className={styles.daySection}>
      <div className={styles.dayLabel}>
        <span className={styles.dayLabelPill}>{label}</span>
        <span className={styles.dayLabelRail} />
      </div>
      <div className={styles.dayActs}>
        {past.length > 0 ? (
          <>
            <button
              type="button"
              className={styles.pastToggle}
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>▾</span>
              {expanded
                ? `${past.length} vergangene ${past.length === 1 ? 'Act' : 'Acts'} ausblenden`
                : `${past.length} vergangene ${past.length === 1 ? 'Act' : 'Acts'} anzeigen`}
            </button>
            {expanded
              ? past.map((act) => <ActCard key={act.id} slug={slug} act={act} />)
              : null}
          </>
        ) : null}
        {visible.map((act) => (
          <ActCard key={act.id} slug={slug} act={act} />
        ))}
      </div>
    </section>
  );
}
